import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { RecruitmentRoom, CustomRecruitmentRoom } from "./Facilities";

/**
 * ### 入所申込テンプレート API 操作クラス
 */
export default class AdmissionApplicationTemplates {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET AdmissionApplicationTemplates
   */
  public async get(userStore: LoginUser): Promise<AdmissionApplicationTemplatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AdmissionApplicationTemplate, formatParams);

    const response = await this.commonService.executeGet<AdmissionApplicationTemplatesGetModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET AdmissionApplicationTemplates
   */
  public async gets(userStore: LoginUser): Promise<Array<AdmissionApplicationTemplatesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AdmissionApplicationTemplate, formatParams);

    const response = await this.commonService.executeGet<Array<AdmissionApplicationTemplatesGetModel>>(
      userStore,
      `${requestUrl}/all`,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }
}

export interface AdmissionApplicationTemplatesGetModel {
  /** システムで一意に特定するための ID */
  id: number;
  /** テンプレート名 */
  name: string;
  /** 説明文 */
  information: string;
  /** 全施設で使用可能か否か */
  canUseAll: boolean;
  /** 施設 ID を配列で持つ */
  canUseFacilities: number[];
  /** テンプレート情報の Object */
  template: Template;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 作成日時 (YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface Template {
  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export interface TemplateItem {
  /** テンプレート種別 */
  type: TemplateType;
  /** テンプレート内で一意の ID */
  id: string;
  /** 見出し */
  label: string;
  /** 見出し表示可否 */
  showLabel: boolean;
  /** 説明文 */
  text?: string;
  /** 強調説明文 */
  strongText: string;
  /** ヘルプテキスト */
  helpText: string;
  /** ユーザーに表示するか否か */
  displayToUser: boolean;
}

export enum TemplateType {
  SECTION = "section",
  GROUP = "group",
  TEXT = "text",
  RADIO = "radio",
  RADIO_GROUP = "radioGroup",
  SELECT = "select",
  CHECKBOX = "checkbox",
  MULTIPLE_CHECKBOX = "multipleCheckbox",
  SWITCH_CHECKBOX = "switchCheckbox",
  TEXTAREA = "textarea",
  ADDRESS = "address",
  BIRTHDAY = "birthday",
  YEAR_MONTH_DATE = "yearMonthDate",
  YEAR_MONTH = "yearMonth",
  ROOM_TYPE = "roomType",
  FILE = "file",
  TEL = "tel",
  NAME = "name"
}

export interface TemplateItemSection extends TemplateItem {
  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export interface TemplateItemGroup extends TemplateItem {
  /** グループの追加ボタンを表示するか否か */
  displayAddButton: boolean;
  /** グループの追加ボタン名 */
  displayAddButtonLabel: string;
  /** 追加ボタンで追加出来る数の上限。displayAddButton が true の場合は必須 */
  max?: number;
  /** グルーピングするテンプレート情報、追加時にこのプロパティをコピーしてpropertiesに追加する */
  template: Array<TemplateItem>;

  /** テンプレートアイテム */
  properties: Array<Array<TemplateItem>>;
}

export interface TemplateItemInput extends TemplateItem {
  /** バリデーションルール */
  validationRules: string[];
}

export interface TemplateItemText extends TemplateItemInput {
  /** placeholder 属性 */
  placeholder?: string;
  /** type 属性。type 属性で入力可能なものは全て使用可能 */
  inputType?: string;
  /** max 属性 */
  max?: number;
  /** min 属性 */
  min?: number;
  /** maxlength 属性 */
  maxlength?: number;
  /** minlength 属性 */
  minlength?: number;
  /** 入力項目の前に付与する文字列 */
  prefix?: string;
  /** 入力項目の後に付与する文字列 */
  suffix?: string;
  /** width 属性 */
  width: number;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface TemplateItemRadio extends TemplateItemInput {
  /** 選択肢 */
  options: Array<RadioOption>;
  /** 配置の向き */
  align: RadioAlign;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface RadioOption {
  /** 画面表示項目名 */
  text: string;
  /** 値 */
  value: string;
}

export enum RadioAlign {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal"
}

export interface TemplateItemRadioGroup extends TemplateItemInput {
  /** テンプレートアイテム */
  properties: Array<RadioGroup>;

  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface RadioGroup {
  /** 選択肢 */
  option: RadioOption;

  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export interface TemplateItemSelect extends TemplateItemInput {
  /** 選択肢 */
  options: Array<SelectOption>;
  /** 複数選択可能か否か */
  multiple: boolean;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string[];
}

export interface SelectOption {
  /** 画面表示項目名 */
  text: string;
  /** 値 */
  value: string;
}

export interface TemplateItemCheckbox extends TemplateItemInput {
  /** 質問内容 */
  question: string;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: boolean;
}

export interface TemplateItemMultipleCheckbox extends TemplateItemInput {
  /** 質問内容 */
  questions: string[];
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: boolean[];
}

export interface TemplateItemSwitchCheckbox extends TemplateItemInput {
  /** 質問内容 */
  question: string;
  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
  /** 表示条件 */
  showRule: boolean;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: boolean;
}

export interface TemplateItemTextarea extends TemplateItemInput {
  /** placeholder 属性 */
  placeholder?: string;
  /** rows 属性 */
  rows?: number;
  /** no-resize 属性 */
  noResize?: boolean;
  /** maxlength 属性 */
  maxlength?: number;
  /** minlength 属性 */
  minlength?: number;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface TemplateItemAddress extends TemplateItemInput {
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: AddressAnswer;
}

export interface AddressAnswer {
  /** 郵便番号 */
  postalCode: string;
  /** 住所 */
  address: string;
}

export interface TemplateItemBirthday extends TemplateItemInput {
  /** 選択肢の開始年を現在年から何年前にするか */
  startYearRange: number;
  /** 選択肢の終了年を現在年から何年後にするか */
  endYearRange: number;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: BirthdayAnswer;
}

export interface BirthdayAnswer {
  /** 年 */
  year: string;
  /** 月 */
  month: string;
  /** 日 */
  date: string;
}

export interface TemplateItemYearMonthDate extends TemplateItemInput {
  /** 選択肢の開始年を現在年から何年前にするか */
  startYearRange: number;
  /** 選択肢の終了年を現在年から何年後にするか */
  endYearRange: number;
  /** 入力項目の後に付与する文字列 */
  suffix?: string;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: YearMonthDateAnswer;
}

export interface YearMonthDateAnswer {
  /** 年 */
  year: string;
  /** 月 */
  month: string;
  /** 日 */
  date: string;
}

export interface TemplateItemYearMonth extends TemplateItemInput {
  /** 選択肢の開始年を現在年から何年前にするか */
  startYearRange: number;
  /** 選択肢の終了年を現在年から何年後にするか */
  endYearRange: number;
  /** 入力項目の後に付与する文字列 */
  suffix?: string;
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: YearMonthAnswer;
}

export interface YearMonthAnswer {
  /** 年 */
  year: string;
  /** 月 */
  month: string;
}

export interface TemplateItemFile extends TemplateItemInput {
  /** 許容する MIME タイプ */
  accept?: string[];
  /** ファイル数上限 */
  maxlength: number;
  /** ファイル情報 */
  tempAnswer: Array<{ file: File; fileName: string }> | null;
  /** ファイル情報 */
  answer: Array<FileAnswer> | null;
}

export interface FileAnswer {
  /** ファイルが格納されている S3 の URL */
  url: string;
  /** ファイル名 */
  name: string;
}

export interface TemplateItemRoomType extends TemplateItemInput {
  answer: Array<RoomType>;
}

export interface RoomType {
  /** 施設のID */
  facilityId: number;
  /** 質問内容回答 */
  recruitmentRoom: RecruitmentRoom;
  /** 質問内容回答(施設固有) */
  customRecruitmentRoom?: Array<CustomRecruitmentRoom>;
}

export interface TemplateItemTel extends TemplateItemInput {
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: TelAnswer;
}

export interface TelAnswer {
  /** 電話番号1 */
  first: string;
  /** 電話番号2 */
  second: string;
  /** 電話番号3 */
  third: string;
}

export interface TemplateItemName extends TemplateItemInput {
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: NameAnswer;
}

export interface NameAnswer {
  /** 名 */
  firstName: string;
  /** 性 */
  secondName: string;
  /** めい */
  firstKanaName: string;
  /** せい */
  secondKanaName: string;
}
